/**
 * ANANT INNER SELF GUIDANCE — Osho Zen Tarot
 * Route: /anant-tarot
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import {
  Sparkles, Moon, Sun as SunIcon, ShieldCheck, Volume2, VolumeX,
  Share2, Wallet, RefreshCw, Heart, Star as StarIcon, Eye,
  ArrowRight, MessageCircle, Send, Twitter, Facebook, Link2, Lock, Shuffle, Download, Flame, Globe,
  Mic, MicOff, Info
} from 'lucide-react';
import api from '../../utils/api';
import { cardRoman } from './tarotRoman';
import { useAuth } from '../../context/AuthContext';
import BuyATBalanceModal from '../../components/BuyATBalanceModal';
import SponsoredTools from '../../components/SponsoredTools';

const BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const AnantTarot = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [publicSettings, setPublicSettings] = useState({
    whatsapp_cta_url: 'https://wa.me/919579369830?text=TAROT',
    whatsapp_cta_label: 'Need personal guidance? Chat on WhatsApp'
  });
  const [cards, setCards] = useState([]);
  const [wallet, setWallet] = useState({ at_balance: 0 });
  const [freeDraw, setFreeDraw] = useState(null);
  const [question, setQuestion] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [voiceListening, setVoiceListening] = useState(false);
  const recognitionRef = useRef(null);
  const [view, setView] = useState('intro');
  const [speaking, setSpeaking] = useState(false);
  const [showBuyAT, setShowBuyAT] = useState(false);

  // New Phase: language, shuffle, prayer, streak
  const [languages, setLanguages] = useState([]);
  const [language, setLanguage] = useState('en');
  const [shuffleCount, setShuffleCount] = useState(0);
  const [shuffleSeed, setShuffleSeed] = useState(0); // changes to re-render grid
  const [prayerActive, setPrayerActive] = useState(false);
  const [prayerSecondsLeft, setPrayerSecondsLeft] = useState(60);
  const [prayedAt, setPrayedAt] = useState(null);
  const [streak, setStreak] = useState(null);

  const speechRef = useRef(null);
  const prayerTimerRef = useRef(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, c, w, h, l, st, ps] = await Promise.all([
        api.get('/anant-tarot/status'),
        api.get('/anant-tarot/cards'),
        api.get('/anant-tarot/wallet'),
        api.get('/anant-tarot/my-readings?limit=20'),
        api.get('/anant-tarot/languages'),
        api.get('/anant-tarot/streak'),
        api.get('/anant-tarot/public-settings'),
      ]);
      setStatus(s.data);
      setCards(c.data.cards || []);
      setWallet(w.data);
      setFreeDraw(s.data.free_card_today);
      setHistory(h.data.readings || []);
      setLanguages(l.data.languages || []);
      setStreak(st.data);
      setPublicSettings(prev => ({ ...prev, ...(ps.data || {}) }));
    } catch (err) {
      console.warn('tarot load failed', err);
      toast.error('Failed to load Tarot module');
    } finally { setLoading(false); }
  };

  // Track referral click (fire-and-forget)
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const refCode = url.searchParams.get('ref');
      if (refCode && refCode.trim()) {
        api.post(`/referral/track-click/${encodeURIComponent(refCode.trim())}`).catch(() => {});
      }
    } catch (_) {}
  }, []);

  // ===== Prayer Timer =====
  const startPrayer = () => {
    setPrayerActive(true);
    setPrayerSecondsLeft(60);
    setPrayedAt(null);
    if (prayerTimerRef.current) clearInterval(prayerTimerRef.current);
    prayerTimerRef.current = setInterval(() => {
      setPrayerSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(prayerTimerRef.current);
          setPrayerActive(false);
          setPrayedAt(new Date().toISOString());
          toast.success('🙏 Prayer complete. The universe is listening.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  const skipPrayer = () => {
    if (prayerTimerRef.current) clearInterval(prayerTimerRef.current);
    setPrayerActive(false);
    setPrayedAt(new Date().toISOString());
  };
  useEffect(() => () => {
    if (prayerTimerRef.current) clearInterval(prayerTimerRef.current);
  }, []);

  // ===== Shuffle =====
  const [isShuffling, setIsShuffling] = useState(false);
  const handleShuffle = () => {
    if (isShuffling) return;
    setIsShuffling(true);
    setSelectedIds([]);
    // Brief animation phase (700ms) then commit new order
    setTimeout(() => {
      setShuffleCount(c => c + 1);
      setShuffleSeed(s => s + Math.random());
      setIsShuffling(false);
      toast.success(`✨ Shuffle ${shuffleCount + 1}/3 ${shuffleCount + 1 >= 3 ? '— You may now select cards' : ''}`);
    }, 700);
  };

  // Derived: cards shuffled order based on shuffleSeed
  const shuffledCards = useMemo(() => {
    if (shuffleCount === 0) return cards;
    const arr = [...cards];
    let seed = shuffleSeed;
    for (let i = arr.length - 1; i > 0; i--) {
      seed = (seed * 9301 + 49297) % 233280;
      const j = Math.floor((seed / 233280) * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [cards, shuffleCount, shuffleSeed]);

  const canSelectCards = shuffleCount >= 3 && prayedAt;

  const handleDrawFree = async () => {
    setBusy(true);
    try {
      const r = await api.post('/anant-tarot/draw-free');
      setFreeDraw(r.data.draw);
      if (r.data.already_drawn) {
        toast.info('You have already drawn today. Come back tomorrow ✨');
      } else {
        toast.success(`✨ Your Card of the Day: ${r.data.draw.card.name}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Draw failed');
    } finally { setBusy(false); }
  };

  const toggleSelect = (id) => {
    if (!canSelectCards) {
      toast.info(shuffleCount < 3 ? `Shuffle ${3 - shuffleCount} more time(s) first` : 'Complete the 60s prayer first 🙏');
      return;
    }
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 5) {
        toast.info('You may only select 5 cards.');
        return prev;
      }
      return [...prev, id];
    });
  };

  // ============ Voice Input (Web Speech API) ============
  const toggleVoiceInput = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error('Voice input not supported on this browser. Please type your question.');
      return;
    }
    if (voiceListening) {
      try { recognitionRef.current?.stop(); } catch (e) { /* ignore */ }
      setVoiceListening(false);
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    // Try language-aware recognition
    rec.lang = language === 'hi' ? 'hi-IN' : language === 'mr' ? 'mr-IN' : 'en-IN';
    rec.onresult = (ev) => {
      let txt = '';
      for (let i = 0; i < ev.results.length; i++) txt += ev.results[i][0].transcript;
      setQuestion(txt);
    };
    rec.onerror = () => { setVoiceListening(false); toast.error('Voice input error. Try typing instead.'); };
    rec.onend = () => setVoiceListening(false);
    recognitionRef.current = rec;
    try { rec.start(); setVoiceListening(true); }
    catch (e) { setVoiceListening(false); toast.error('Could not start voice. Try typing.'); }
  };

  const handleSpread = async () => {
    if (!question.trim() || question.trim().length < 3) {
      toast.error('Please type a question first.');
      return;
    }
    if (selectedIds.length !== 5) {
      toast.error('Please select exactly 5 cards.');
      return;
    }
    const cost = status?.spread_cost_at || 100;
    if ((wallet.at_balance || 0) < cost) {
      toast.error(`Insufficient AT balance. Need ${cost} AT, you have ${wallet.at_balance}.`);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/anant-tarot/spread', {
        question: question.trim(),
        selected_card_ids: selectedIds,
        language,
        shuffle_count: shuffleCount,
        prayed_at: prayedAt,
      });
      setReading(r.data.reading);
      setView('reading');
      setWallet(w => ({ ...w, at_balance: r.data.reading.user_new_balance }));
      // Reset for next spread
      setShuffleCount(0);
      setPrayedAt(null);
      setSelectedIds([]);
      // Refresh history in background
      api.get('/anant-tarot/my-readings?limit=20').then(h => setHistory(h.data.readings || []));
      // Refresh streak (paid spread now counts toward streak)
      api.get('/anant-tarot/streak').then(st => setStreak(st.data)).catch(() => { });
      toast.success('Your reading is ready ✨');
      if (r.data.streak_bonus) {
        toast.success(`🔥 STREAK BONUS! Day ${r.data.streak_bonus.milestone} = +${r.data.streak_bonus.bonus_at} AT credited!`, { duration: 7000 });
      }
      if (r.data.referral_bonus_awarded) {
        toast.success(`🎁 Your referrer earned ${r.data.referral_bonus_awarded.bonus_at} AT thanks to your first spread!`, { duration: 6000 });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 402 || (detail && /insufficient/i.test(String(detail)))) {
        toast.error(`💰 Insufficient AT balance. You need ${cost} AT · Please top up from Trading → Cash Deposit`, { duration: 6000 });
      } else if (status === 400) {
        toast.error(detail || 'Please pick 5 different cards and add a real question');
      } else {
        toast.error(`Spread failed: ${detail || err.message || 'Please retry in a few seconds'}`, { duration: 6000 });
      }
      // Refresh wallet to reflect any partial state
      try { const w = await api.get('/wallets/me'); setWallet(w.data); } catch (_) { /* silent */ }
    } finally { setBusy(false); }
  };

  const audioRef = useRef(null);
  const speakReading = async () => {
    if (!reading?.reading_text) return;
    if (speaking) {
      try { window.speechSynthesis.cancel(); } catch (_) {}
      if (audioRef.current) { try { audioRef.current.pause(); audioRef.current.src = ''; } catch (_) {} }
      setSpeaking(false);
      return;
    }
    // ✅ PRIMARY — server-side gTTS MP3 (works on ALL devices, no browser speechSynthesis dependency)
    if (reading.id) {
      try {
        setSpeaking(true);
        const r = await api.get(`/anant-tarot/tts/${reading.id}.mp3`, { responseType: 'blob' });
        const blobUrl = URL.createObjectURL(r.data);
        const audio = new Audio(blobUrl);
        audio.volume = 1.0;
        audio.onended = () => { setSpeaking(false); try { URL.revokeObjectURL(blobUrl); } catch (_) {} };
        audio.onerror = () => { setSpeaking(false); toast.error('MP3 playback failed, switching to browser voice…'); browserFallback(); };
        audioRef.current = audio;
        await audio.play();
        return;
      } catch (e) {
        console.warn('Server TTS failed, falling back to browser', e);
        setSpeaking(false);
        toast.message('Generating audio… using browser voice');
        // fall through to browser TTS
      }
    }
    browserFallback();
  };

  const browserFallback = () => {
    if (!reading?.reading_text) return;
    try { window.speechSynthesis.resume(); } catch (_) {}
    const text = reading.reading_text.replace(/\*\*/g, '').replace(/[#]/g, '').trim();
    if (!text) { setSpeaking(false); return; }
    const lang = languages.find(l => l.code === (reading.language || 'en'));
    const targetLocale = lang?.tts_locale || 'hi-IN';
    const start = (voices) => {
      const utter = new SpeechSynthesisUtterance(text);
      utter.rate = 0.92; utter.pitch = 1.0; utter.volume = 1.0; utter.lang = targetLocale;
      const exact = voices.find(v => v.lang === targetLocale);
      const prefix = voices.find(v => v.lang && v.lang.startsWith(targetLocale.split('-')[0]));
      const chosen = exact || prefix || voices.find(v => v.default) || voices[0];
      if (chosen) utter.voice = chosen;
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => { setSpeaking(false); toast.error('Browser voice unavailable on this device'); };
      speechRef.current = utter;
      setSpeaking(true);
      try { window.speechSynthesis.cancel(); } catch (_) {}
      window.speechSynthesis.speak(utter);
      const keepAlive = setInterval(() => {
        if (!window.speechSynthesis.speaking) { clearInterval(keepAlive); return; }
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }, 10000);
    };
    let voices = window.speechSynthesis.getVoices();
    if (voices && voices.length > 0) start(voices);
    else {
      const onV = () => {
        voices = window.speechSynthesis.getVoices();
        window.speechSynthesis.removeEventListener('voiceschanged', onV);
        if (voices && voices.length > 0) start(voices);
        else toast.error('No voices on this device — please install a system voice for ' + targetLocale);
      };
      window.speechSynthesis.addEventListener('voiceschanged', onV);
      setTimeout(() => { const v = window.speechSynthesis.getVoices(); if (v && v.length > 0 && !speechRef.current) start(v); }, 1500);
    }
  };

  const downloadPdf = () => {
    if (!reading) return;
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 40;
      let y = 50;
      // Header
      doc.setFontSize(20);
      doc.setTextColor(139, 69, 199);
      doc.text('Anant Inner Self — Tarot Reading', margin, y);
      y += 25;
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated ${new Date(reading.created_at).toLocaleString()}`, margin, y);
      y += 15;
      doc.text(`Language: ${languages.find(l => l.code === reading.language)?.name || 'English'}`, margin, y);
      y += 25;
      // Question
      doc.setFontSize(13);
      doc.setTextColor(20);
      doc.text('Your Question:', margin, y);
      y += 18;
      doc.setFontSize(11);
      const qLines = doc.splitTextToSize(reading.question || '', pageWidth - 2 * margin);
      doc.text(qLines, margin, y);
      y += qLines.length * 14 + 10;
      // Cards
      doc.setFontSize(13);
      doc.text('Cards Drawn:', margin, y);
      y += 18;
      doc.setFontSize(10);
      const roles = ['Present Energy', 'Illusion', 'Outside Influence', 'Path of Action', 'Awakening'];
      (reading.cards || []).forEach((c, i) => {
        doc.text(`${i + 1}. ${c.name} — ${roles[i]}`, margin + 10, y);
        y += 14;
      });
      y += 10;
      // Reading text
      doc.setFontSize(12);
      doc.setTextColor(20);
      doc.text('Reading:', margin, y);
      y += 16;
      doc.setFontSize(10);
      // Strip markdown bold markers; PDF won't render unicode for all Indian scripts reliably
      // but English/transliteration fallback works. For full Devanagari support we'd need to embed a font.
      const cleanText = reading.reading_text.replace(/\*\*(.+?)\*\*/g, '$1');
      const lines = doc.splitTextToSize(cleanText, pageWidth - 2 * margin);
      lines.forEach(line => {
        if (y > 780) { doc.addPage(); y = 50; }
        doc.text(line, margin, y);
        y += 13;
      });
      // Footer
      if (y > 760) { doc.addPage(); y = 50; }
      y += 20;
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text('— Anant Inner Self Guidance · anantwebportal.com —', margin, y);

      doc.save(`anant-reading-${reading.id.slice(0, 8)}.pdf`);
      toast.success('Reading downloaded as PDF ✓');
    } catch (e) {
      toast.error('PDF download failed');
    }
  };

  const openShare = async (readingId) => {
    try {
      const r = await api.get(`/anant-tarot/share-link/${readingId}`);
      setShareData(r.data);
      setShowShare(true);
    } catch (err) { toast.error('Share link failed'); }
  };

  const restart = () => {
    setQuestion('');
    setSelectedIds([]);
    setReading(null);
    setShuffleCount(0);
    setPrayedAt(null);
    setShuffleSeed(0);
    setView('spread');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-950 via-fuchsia-950 to-indigo-950">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-fuchsia-300 mx-auto animate-pulse" />
          <div className="text-fuchsia-200 mt-3">Awakening the Zen oracle…</div>
        </div>
      </div>
    );
  }

  const cost = status?.spread_cost_at || 100;
  const canAfford = (wallet.at_balance || 0) >= cost;

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 px-4 bg-gradient-to-br from-violet-950 via-fuchsia-950 to-indigo-950 text-white relative overflow-hidden" data-testid="anant-tarot-page">
      {/* Mystical glow blobs */}
      <div className="pointer-events-none fixed top-1/4 -left-32 w-96 h-96 rounded-full bg-fuchsia-500/20 blur-3xl animate-pulse" />
      <div className="pointer-events-none fixed bottom-1/4 -right-32 w-96 h-96 rounded-full bg-violet-500/20 blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      <div className="pointer-events-none fixed top-2/3 left-1/2 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8">
          <div>
            <div className="text-fuchsia-300 text-xs sm:text-sm font-semibold uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
              <Moon className="w-4 h-4" /> Anant Inner Self Guidance
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold bg-gradient-to-r from-fuchsia-300 via-violet-300 to-amber-300 bg-clip-text text-transparent leading-tight">
                Osho Zen Tarot
              </h1>
              <img
                src={`${BACKEND}/static/tarot_extras/universal_energy.png`}
                alt="Universal Energy"
                data-testid="universal-energy-img"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border-2 border-amber-400/40 shadow-lg shadow-amber-500/30 hover:scale-110 transition cursor-pointer"
                title="Universal Energy — the supreme cosmic source"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <p className="text-fuchsia-100/70 mt-2 text-sm sm:text-base max-w-xl">
              {status?.welcome_message || 'Ask the universe a question and let the cards reflect your inner truth.'}
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-3 flex-wrap">
            {streak && (
              <div className="px-3 py-2 rounded-xl bg-orange-500/10 border border-orange-400/30 flex items-center gap-2" data-testid="tarot-streak-badge">
                <Flame className={`w-4 h-4 ${streak.current_streak > 0 ? 'text-orange-300 animate-pulse' : 'text-slate-500'}`} />
                <div>
                  <div className="text-[10px] text-orange-300/70 uppercase tracking-wider">Paid Streak (100+ AT/day)</div>
                  <div className="text-sm font-extrabold text-orange-200">
                    {streak.current_streak || 0} day{(streak.current_streak || 0) !== 1 ? 's' : ''}
                    <span className="text-[10px] text-orange-300/60 ml-1">· next +{streak.next_milestone_reward_at}AT @ day {streak.next_milestone}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="px-4 py-2 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center gap-2" data-testid="tarot-wallet-badge">
              <Wallet className="w-4 h-4 text-amber-300" />
              <div>
                <div className="text-[10px] text-amber-300/70 uppercase tracking-wider">Your AT Balance</div>
                <div className="text-base font-extrabold text-amber-200">{(wallet.at_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} AT</div>
              </div>
            </div>
            <button
              onClick={() => setShowBuyAT(true)}
              data-testid="buy-at-balance-btn"
              className="px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold text-xs sm:text-sm hover:scale-105 transition inline-flex items-center gap-1.5"
              title="Add AT balance (1 INR = 1 AT)"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Buy AT
            </button>
            <button
              onClick={loadAll}
              data-testid="tarot-refresh"
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition"
            ><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-6 text-xs text-fuchsia-200/60 italic border-l-2 border-fuchsia-400/40 pl-3 flex items-start gap-2">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{status?.legal_disclaimer || 'Spiritual guidance for self-reflection. Not a substitute for professional advice.'}</span>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'intro', label: 'Card of the Day', icon: SunIcon },
            { id: 'spread', label: '5-Card Spread', icon: Sparkles },
            { id: 'reading', label: 'Latest Reading', icon: Eye },
            { id: 'history', label: 'My Readings', icon: StarIcon },
          ].map(t => {
            const Icon = t.icon;
            const active = view === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setView(t.id)}
                data-testid={`tarot-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
                  active
                    ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-lg shadow-fuchsia-500/30'
                    : 'bg-white/5 hover:bg-white/10 text-fuchsia-100/80'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* ============ INTRO / FREE DRAW ============ */}
        {view === 'intro' && (
          <FreeDrawView freeDraw={freeDraw} onDraw={handleDrawFree} busy={busy} status={status} onStart={() => setView('spread')} user={user} language={language} setLanguage={setLanguage} languages={languages} />
        )}

        {/* ============ SPREAD ============ */}
        {view === 'spread' && (
          <div data-testid="tarot-spread-view">
            <Disclaimer />
            {/* Step-by-step instructions for 5-card reading */}
            <TarotSpreadInstructions language={language} />
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6 space-y-4">
              {/* Question + Language */}
              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <label className="text-fuchsia-200 text-sm font-semibold uppercase tracking-wider block mb-2">Your Question</label>
                  <div className="text-sm sm:text-base text-amber-100 bg-gradient-to-br from-amber-500/15 to-rose-500/15 border-2 border-amber-400/40 rounded-lg p-3 sm:p-4 mb-3 leading-relaxed" data-testid="tarot-question-help">
                    <div className="font-extrabold text-amber-300 mb-1.5 text-base sm:text-lg">📝 IMPORTANT: Apna naam zaroor likhein sawaal ke saath</div>
                    <div className="text-amber-100/95 mb-2">
                      Sahi personalized reading ke liye — apne sawaal mein apna <b className="text-amber-200">naam</b> zaroor mention karein.
                    </div>
                    <div className="text-xs sm:text-sm text-fuchsia-100/90 italic bg-slate-950/50 rounded-md p-2 border border-fuchsia-400/20">
                      <b className="text-amber-300 not-italic">Example:</b> "Hello Master, mera naam <span className="text-amber-200 font-bold">Priya</span> hai. Mujhe bataiye ki mera kal college exam hai — result kaisa aa sakta hai?"
                    </div>
                  </div>
                  <div className="text-[11px] text-fuchsia-100/70 bg-violet-950/40 border border-fuchsia-400/20 rounded-lg p-2.5 mb-2 leading-relaxed">
                    💡 Aap apne <b className="text-amber-200">life</b> ya <b className="text-amber-200">universe</b> se related koi bhi sawaal pooch sakte ho — shaadi, career, relationships, family, kids, business, decisions, planning, vacations, results, sports, politics, day-ahead etc. Type karein ya 🎙️ button daba ke bolein.
                  </div>
                  <div className="relative">
                    <textarea
                      value={question}
                      onChange={e => setQuestion(e.target.value)}
                      placeholder="Hello Master, mera naam ___ hai. Mujhe bataiye ki..."
                      rows={4}
                      data-testid="tarot-question-input"
                      className="w-full bg-violet-950/50 border border-fuchsia-400/30 rounded-xl px-4 py-3 pr-12 text-white placeholder-fuchsia-300/30 focus:outline-none focus:border-fuchsia-400 text-base"
                    />
                    <button
                      type="button"
                      onClick={toggleVoiceInput}
                      data-testid="tarot-voice-input-btn"
                      title={voiceListening ? 'Stop listening' : 'Speak your question'}
                      className={`absolute top-2 right-2 w-9 h-9 rounded-full flex items-center justify-center transition ${voiceListening ? 'bg-rose-500 text-white animate-pulse shadow-lg shadow-rose-500/40' : 'bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500/40'}`}
                    >
                      {voiceListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                  </div>
                  {voiceListening && (
                    <div className="text-[10px] text-rose-200 mt-1 italic flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" /> Listening… bolein abhi
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-fuchsia-200 text-sm font-semibold uppercase tracking-wider mb-2 flex items-center gap-1"><Globe className="w-4 h-4" /> Language</label>
                  <select
                    value={language}
                    onChange={e => setLanguage(e.target.value)}
                    data-testid="tarot-language-select"
                    className="w-full bg-violet-950/50 border border-fuchsia-400/30 rounded-xl px-4 py-3 text-white"
                  >
                    {languages.map(l => (
                      <option key={l.code} value={l.code}>{l.native} ({l.name})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Ritual: Prayer + Shuffle */}
              <div className="grid md:grid-cols-2 gap-3">
                <div className={`rounded-xl border p-3 ${prayedAt ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-violet-950/40 border-fuchsia-400/30'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase font-bold tracking-wider text-fuchsia-200 flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> Step 1 · 60s Prayer</div>
                    {prayedAt && <span className="text-emerald-300 text-xs font-bold">✓ Complete</span>}
                  </div>
                  {prayerActive ? (
                    <div className="text-center">
                      <div className="text-4xl font-extrabold text-amber-300 mb-1 tabular-nums">{prayerSecondsLeft}s</div>
                      <div className="text-xs text-fuchsia-100/70 italic mb-2">Close your eyes · Focus on your question · Breathe slowly</div>
                      <button onClick={skipPrayer} data-testid="tarot-prayer-skip" className="text-xs text-fuchsia-300 underline">Skip prayer</button>
                    </div>
                  ) : !prayedAt ? (
                    <button onClick={startPrayer} data-testid="tarot-prayer-start" className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-fuchsia-500/30">
                      <Heart className="w-4 h-4" /> Begin 60s Prayer
                    </button>
                  ) : (
                    <div className="text-emerald-200 text-sm italic">🙏 The universe is ready to answer.</div>
                  )}
                </div>

                <div className={`rounded-xl border p-3 ${shuffleCount >= 3 ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-violet-950/40 border-fuchsia-400/30'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase font-bold tracking-wider text-fuchsia-200 flex items-center gap-1"><Shuffle className="w-3.5 h-3.5" /> Step 2 · Shuffle (min 3)</div>
                    <span className={`text-xs font-bold ${shuffleCount >= 3 ? 'text-emerald-300' : 'text-amber-300'}`}>{shuffleCount}/3</span>
                  </div>
                  <button onClick={handleShuffle} data-testid="tarot-shuffle-btn" className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-amber-500/30">
                    <Shuffle className="w-4 h-4" /> Shuffle the deck
                  </button>
                  <div className="text-[10px] text-fuchsia-200/60 text-center mt-2">{shuffleCount >= 3 ? '✓ Deck ready' : `Shuffle ${3 - shuffleCount} more time(s) to unlock card selection`}</div>
                </div>
              </div>

              {/* Selection summary + Reveal */}
              <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-white/10">
                <div className="text-sm text-fuchsia-100/70">
                  Step 3: <span className="font-bold text-amber-300">{selectedIds.length}/5</span> cards selected · Cost: <span className="font-bold text-amber-300">{cost} AT</span>
                </div>
                <button
                  onClick={handleSpread}
                  disabled={busy || selectedIds.length !== 5 || !question.trim()}
                  data-testid="tarot-reveal-btn"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 via-violet-500 to-amber-500 text-white font-bold shadow-lg shadow-fuchsia-500/40 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 hover:shadow-fuchsia-500/60 transition"
                >
                  {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {busy ? 'Reading the universe…' : (canAfford ? `Reveal Reading (${cost} AT)` : 'Insufficient AT')}
                </button>
              </div>
              {!canAfford && (
                <div className="text-xs text-amber-300 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Top up your wallet — 1 AT = ₹1. Use Cash Deposit from Trading panel.
                </div>
              )}
              {!canSelectCards && (
                <div className="text-xs text-fuchsia-200/70 italic bg-white/5 rounded-lg p-2 border border-fuchsia-400/20">
                  ✦ Complete <strong>Step 1 (60s prayer)</strong> and <strong>Step 2 (3 shuffles)</strong> to unlock card selection below.
                </div>
              )}
            </div>

            <div className={canSelectCards ? '' : 'opacity-50 pointer-events-none'}>
              <CardGrid cards={shuffledCards} selectedIds={selectedIds} onToggle={toggleSelect} isShuffling={isShuffling} />
            </div>
          </div>
        )}

        {/* ============ READING ============ */}
        {view === 'reading' && reading && (
          <ReadingView
            reading={reading}
            languages={languages}
            onRestart={restart}
            onShare={() => openShare(reading.id)}
            onSpeak={speakReading}
            onDownload={downloadPdf}
            speaking={speaking}
            publicSettings={publicSettings}
          />
        )}
        {view === 'reading' && !reading && (
          <div className="text-center py-16 text-fuchsia-100/60">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <div>No reading yet. Start a 5-card spread to begin.</div>
            <button onClick={() => setView('spread')} className="mt-4 px-5 py-2 rounded-xl bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-200">Start Spread</button>
          </div>
        )}

        {/* ============ HISTORY ============ */}
        {view === 'history' && (
          <HistoryView history={history} onView={(r) => { setReading(r); setView('reading'); }} onShare={openShare} />
        )}

        {/* ============ SHARE MODAL ============ */}
        {showShare && shareData && (
          <ShareModal data={shareData} onClose={() => setShowShare(false)} />
        )}

        {/* ============ BUY AT MODAL ============ */}
        <BuyATBalanceModal open={showBuyAT} onClose={() => { setShowBuyAT(false); loadAll(); }} />
      </div>
    </div>
  );
};

// ============ 5-Card Spread Instructions (bilingual) ============
const TAROT_STEPS = {
  en: {
    title: '5-Card Reading — How to use',
    steps: [
      { title: 'Step 1 · Add AT balance', body: 'Click Buy AT and pay via QR / UPI at 1 INR = 1 AT. Upload proof OR contact WhatsApp +91 9579369830 for fast approval.' },
      { title: 'Step 2 · Write your name & question', body: 'In the black text box below, type your name and your specific question or problem.' },
      { title: 'Step 3 · 60s Prayer', body: 'Click the Prayer button, close your eyes for 60 seconds, relax, remember your Guru/Master/God, and silently ask your question.' },
      { title: 'Step 4 · Shuffle the cards', body: 'After the prayer, click Shuffle Cards 3 times to charge the deck with your energy.' },
      { title: 'Step 5 · Pick 5 cards', body: 'Now pick 5 cards from the Tarot deck one by one — click each card to select.' },
      { title: 'Step 6 · Reveal reading', body: 'Click Reveal Reading — your live reading will appear in 10–30 seconds with detailed guidance.' },
    ],
    cta: 'Share your reading on social media & grow your community — Learn, Earn & Share 🌟',
  },
  hi: {
    title: '5-कार्ड रीडिंग — कैसे करें',
    steps: [
      { title: 'Step 1 · AT बैलेंस जोड़ें', body: 'Buy AT पर क्लिक करें और QR / UPI पर 1 INR = 1 AT रेशियो में पेमेंट करें। Fast approval के लिए proof upload करें या WhatsApp +91 9579369830 पर संपर्क करें।' },
      { title: 'Step 2 · नाम और सवाल लिखें', body: 'नीचे के काले text box में अपना नाम और अपना सवाल/समस्या लिखें।' },
      { title: 'Step 3 · Prayer button', body: 'Prayer button पर क्लिक करें और 60 seconds तक अपनी आंखें बंद करके relax करें, अपने गुरु/मास्टर/God का ध्यान करें और अपना सवाल मन में पूछें।' },
      { title: 'Step 4 · Shuffle cards', body: 'Prayer के बाद Shuffle Cards button पर 3 बार क्लिक करें।' },
      { title: 'Step 5 · 5 कार्ड चुनें', body: 'अब Tarot deck में से एक-एक करके कोई भी 5 cards पर क्लिक करें।' },
      { title: 'Step 6 · Reveal Reading', body: 'Reveal Reading पर क्लिक करें — 10-30 seconds में आपकी live reading result के साथ दिखेगी।' },
    ],
    cta: 'अपनी reading social media पर share करें और अपनी community grow करें — Learn, Earn & Share 🌟',
  },
};
const TarotSpreadInstructions = ({ language }) => {
  const lang = language && language.startsWith('hi') ? 'hi' : 'en';
  const t = TAROT_STEPS[lang];
  return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-500/10 via-fuchsia-500/5 to-violet-500/10 p-4 sm:p-5 mb-6" data-testid="tarot-spread-instructions">
      <div className="text-amber-300 text-sm sm:text-base font-extrabold uppercase tracking-widest mb-3 flex items-center gap-2">
        <Info className="w-4 h-4" /> {t.title}
      </div>
      <ol className="space-y-2.5">
        {t.steps.map((s, i) => (
          <li key={i} className="flex gap-3 items-start">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-fuchsia-500 text-slate-950 font-black text-xs flex items-center justify-center mt-0.5">{i + 1}</span>
            <div className="flex-1">
              <div className="text-white font-bold text-sm sm:text-base">{s.title}</div>
              <div className="text-fuchsia-100/80 text-xs sm:text-sm leading-relaxed mt-0.5">{s.body}</div>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-4 text-center text-xs sm:text-sm text-emerald-300 font-semibold bg-emerald-500/10 border border-emerald-400/30 rounded-lg py-2 px-3">
        {t.cta}
      </div>
    </div>
  );
};

// ============ Horoscope Voice Player (TTS + download for sharing) ============
const HoroscopeVoicePlayer = ({ text, language }) => {
  const [audioUrl, setAudioUrl] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);
  const audioRef = React.useRef(null);

  // Clear cached audio blob whenever the text or language changes so users
  // hear the correct voice after toggling reading language.
  React.useEffect(() => {
    setAudioUrl(''); setPlaying(false);
  }, [text, language]);

  const detectLang = () => {
    if (language) return language;
    return /[\u0900-\u097F]/.test(text || '') ? 'hi' : 'en';
  };

  const ensureAudio = async () => {
    if (audioUrl) return audioUrl;
    setLoading(true);
    try {
      const resp = await api.post('/anant-tarot/horoscope-tts',
        { text, lang: detectLang() },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      setAudioUrl(url);
      return url;
    } catch (_) {
      return '';
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = async () => {
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    const url = await ensureAudio();
    if (!url) return;
    if (!audioRef.current) return;
    audioRef.current.src = url;
    try { await audioRef.current.play(); setPlaying(true); } catch (_) { /* autoplay blocked */ }
  };

  const download = async () => {
    const url = await ensureAudio();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'anant-daily-horoscope.mp3';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="flex items-center gap-2" data-testid="horoscope-voice-player">
      <button onClick={togglePlay} disabled={loading} data-testid="horoscope-play-btn"
        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-fuchsia-500 text-white text-xs font-bold hover:shadow-lg hover:shadow-amber-500/40 disabled:opacity-60 transition">
        {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
        {loading ? 'Generating voice…' : playing ? 'Pause voice' : 'Listen (Anant voice)'}
      </button>
      <button onClick={download} data-testid="horoscope-download-btn"
        title="Download MP3 to share on WhatsApp"
        className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 text-amber-300 hover:bg-slate-700 flex items-center justify-center">
        <Download className="w-4 h-4" />
      </button>
      <audio ref={audioRef} onEnded={() => setPlaying(false)} onPause={() => setPlaying(false)} preload="none" />
    </div>
  );
};

// ============ Free Draw ============
const FreeDrawView = ({ freeDraw, onDraw, busy, status, onStart, user, language, setLanguage, languages }) => {
  // Pre-draw info form: user must enter name + confirm date + day of week before revealing.
  const today = new Date();
  const yyyymmdd = today.toISOString().slice(0, 10);
  const weekday = today.toLocaleDateString('en-US', { weekday: 'long' });
  const [formName, setFormName] = React.useState(user?.name || user?.username || '');
  const [formDate, setFormDate] = React.useState(yyyymmdd);
  const [formDay, setFormDay] = React.useState(weekday);
  const [horoscope, setHoroscope] = React.useState(null);
  const [horoscopeBusy, setHoroscopeBusy] = React.useState(false);

  const formReady = Boolean(formName.trim() && formDate.trim() && formDay.trim());

  // Refetch horoscope whenever the card OR language changes so users can toggle
  // between languages after the reveal without redrawing.
  React.useEffect(() => {
    if (!freeDraw?.card) return;
    if (!formReady) return;
    setHoroscopeBusy(true);
    const cardIdent = freeDraw.card.cardId ?? freeDraw.card.slug ?? freeDraw.card.id;
    api.post('/anant-tarot/daily-horoscope', {
      card_id: String(cardIdent),
      name: formName.trim(),
      date: formDate,
      day: formDay,
      language: language || 'en',
    })
      .then((r) => setHoroscope(r.data))
      .catch(() => setHoroscope({ text: `Aaj ka din "${freeDraw.card.name}" ke saath positive energy laayega. ✨`, card_name: freeDraw.card.name }))
      .finally(() => setHoroscopeBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeDraw?.card?.cardId, freeDraw?.card?.slug, freeDraw?.card?.id, language, formName, formDate, formDay]);

  const handleReveal = () => {
    if (!formReady) return;
    onDraw();
  };

  // Build share URLs pointing to the backend /card-og/ endpoint so WhatsApp/
  // Facebook/X/Telegram scrapers show the actual tarot card image (not the
  // Anant logo). Referral auto-appended so each share fuels the viral loop.
  const cardSlug = freeDraw?.card?.slug ?? freeDraw?.card?.cardId ?? freeDraw?.card?.id;
  const referralCode = user?.username || user?.referral_code || '';
  const refQuery = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : '';
  const ogShareUrl = freeDraw?.card
    ? `${BACKEND}/api/anant-tarot/card-og/${cardSlug}${refQuery}`
    : '';
  const title = freeDraw?.card ? `🔮 ${freeDraw.card.name} — Anant Inner Self Tarot` : '';
  const body = (horoscope?.text || freeDraw?.card?.basic_meaning || 'My Card of the Day from Anant Inner Self Tarot').slice(0, 200);
  const text = ogShareUrl ? `${title}\n\n${body}\n\n${ogShareUrl}` : '';
  const waUrl = text ? `https://wa.me/?text=${encodeURIComponent(text)}` : '#';
  const fbUrl = ogShareUrl ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}&quote=${encodeURIComponent(title)}` : '#';
  const twUrl = ogShareUrl ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(title + ' — ' + body)}&url=${encodeURIComponent(ogShareUrl)}` : '#';
  const tgUrl = ogShareUrl ? `https://t.me/share/url?url=${encodeURIComponent(ogShareUrl)}&text=${encodeURIComponent(title + ' — ' + body)}` : '#';

  return (
  <div data-testid="tarot-intro-view" className="grid lg:grid-cols-2 gap-6 items-center">
    <div className="space-y-4">
      <div className="text-fuchsia-200 text-sm uppercase tracking-widest font-semibold flex items-center gap-2">
        <Heart className="w-4 h-4" /> Today&apos;s Free Card
      </div>
      <h2 className="text-3xl md:text-4xl font-bold text-white">Daily inner whisper, free for you 💜</h2>
      <p className="text-fuchsia-100/70 leading-relaxed">
        Fill your details below then reveal the Card of the Day for a personalized AI-powered daily reading.
      </p>

      {/* Pre-draw info form — required before Reveal */}
      {!freeDraw && (
        <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-violet-500/10 p-4 space-y-3" data-testid="tarot-daily-info-form">
          <div className="text-amber-300 text-xs font-bold uppercase tracking-widest">Your Details (required)</div>
          <div>
            <label className="text-xs text-fuchsia-200/80 block mb-1">Your Name</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Registered name"
              data-testid="tarot-daily-name-input"
              className="w-full px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-700 text-white text-sm focus:border-amber-400 focus:outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-fuchsia-200/80 block mb-1">Date</label>
              <input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                data-testid="tarot-daily-date-input"
                className="w-full px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-700 text-white text-sm focus:border-amber-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-fuchsia-200/80 block mb-1">Day</label>
              <input
                type="text"
                value={formDay}
                readOnly
                data-testid="tarot-daily-day-input"
                className="w-full px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-700 text-white text-sm cursor-not-allowed opacity-80"
              />
            </div>
          </div>
          {/* Reading language selector — controls both AI horoscope + voice */}
          <div>
            <label className="text-xs text-fuchsia-200/80 block mb-1 flex items-center gap-1">
              <Globe className="w-3 h-3" /> Reading language
            </label>
            <select
              value={language || 'en'}
              onChange={(e) => setLanguage && setLanguage(e.target.value)}
              data-testid="tarot-daily-language-select"
              className="w-full px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-700 text-white text-sm focus:border-amber-400 focus:outline-none"
            >
              {(languages && languages.length > 0
                ? languages
                : [{ code: 'en', name: 'English' }, { code: 'hi', name: 'Hindi' }]
              ).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        {!freeDraw ? (
          <button
            onClick={handleReveal}
            disabled={busy || !formReady}
            data-testid="tarot-draw-free-btn"
            title={!formReady ? 'Fill your name, date and day first' : 'Reveal Card of the Day'}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-fuchsia-500 text-white font-bold shadow-lg shadow-amber-500/30 flex items-center gap-2 hover:shadow-amber-500/50 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <SunIcon className="w-5 h-5" /> {busy ? 'Drawing…' : 'Reveal Card of the Day'}
          </button>
        ) : (
          <div className="text-fuchsia-200 text-sm bg-white/5 border border-fuchsia-400/30 rounded-xl px-4 py-2">
            ✓ Drawn today: <span className="font-bold text-amber-300">{freeDraw.card.name}</span> — come back tomorrow.
          </div>
        )}
        <button onClick={onStart} data-testid="tarot-start-spread-btn" className="px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-fuchsia-400/30 text-fuchsia-200 font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5" /> Start 5-Card Spread ({status?.spread_cost_at || 100} AT) <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
    <div className="flex flex-col items-center justify-center gap-4">
      {freeDraw ? (
        <div className="relative" data-testid="tarot-free-drawn-card">
          <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-400/30 via-violet-400/20 to-amber-400/20 blur-2xl animate-pulse" />
          <div className="relative w-64 md:w-80 aspect-[2/3] rounded-2xl overflow-hidden shadow-2xl shadow-fuchsia-500/40 border-2 border-fuchsia-400/40">
            <img
              src={freeDraw.card.image_url}
              alt={freeDraw.card.name}
              className="w-full h-full object-cover"
              style={{ filter: 'saturate(1.25) contrast(1.1) brightness(1.05) drop-shadow(0 0 8px rgba(255,200,80,0.25))' }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-violet-950/95 via-violet-950/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
              <div className="text-amber-300 text-xs uppercase tracking-widest mb-1">Today</div>
              <div className="text-white text-xl font-extrabold" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>{freeDraw.card.name}</div>
              {cardRoman(freeDraw.card) && <div className="text-amber-300/90 text-sm font-serif italic tracking-[0.25em] mt-0.5">{cardRoman(freeDraw.card)}</div>}
              <div className="text-fuchsia-100/90 text-xs mt-1 italic" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>{freeDraw.card.basic_meaning}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-400/30 via-violet-400/20 to-amber-400/20 blur-2xl animate-pulse" />
          <div className="relative w-64 md:w-80 aspect-[2/3] rounded-2xl bg-gradient-to-br from-violet-900 via-fuchsia-900 to-indigo-900 border-2 border-fuchsia-400/40 flex items-center justify-center shadow-2xl shadow-fuchsia-500/40">
            <div className="text-center px-6">
              <Moon className="w-16 h-16 text-fuchsia-300 mx-auto mb-3 animate-pulse" />
              <div className="text-fuchsia-200 text-sm font-semibold tracking-widest uppercase">Tap to Draw</div>
            </div>
          </div>
        </div>
      )}
      {/* Share bar — only visible after the card is drawn. Uses backend /card-og/
          endpoint so social scrapers show the actual tarot card artwork. */}
      {freeDraw && (
        <div className="w-full max-w-sm space-y-3">
          {/* AI horoscope — daily reading based on card + user info */}
          <div className="rounded-2xl bg-gradient-to-br from-amber-500/15 via-fuchsia-500/10 to-violet-500/15 border-2 border-amber-400/40 p-4" data-testid="tarot-daily-horoscope">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold flex items-center gap-2">
                <SunIcon className="w-3.5 h-3.5" /> Aaj Ka Din — Daily Horoscope
              </div>
              {setLanguage && (
                <select
                  value={language || 'en'}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="tarot-horoscope-language-select"
                  title="Change reading language"
                  className="text-[10px] bg-slate-900/70 border border-amber-400/30 text-amber-200 rounded px-2 py-0.5 focus:outline-none focus:border-amber-400"
                >
                  {(languages && languages.length > 0
                    ? languages
                    : [{ code: 'en', name: 'English' }, { code: 'hi', name: 'Hindi' }]
                  ).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                </select>
              )}
            </div>
            {horoscopeBusy ? (
              <div className="text-fuchsia-200/70 text-xs flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                AI aapki reading likh raha hai…
              </div>
            ) : (
              <>
                <div className="text-white text-sm leading-relaxed whitespace-pre-line mb-3">
                  {horoscope?.text || `Aaj ka din "${freeDraw.card.name}" ke saath positive energy laayega. ✨`}
                </div>
                {horoscope?.text && (
                  <HoroscopeVoicePlayer text={horoscope.text} language={language || 'en'} />
                )}
              </>
            )}
          </div>

          {/* Share bar — only visible after the card is drawn. Uses backend /card-og/
              endpoint so social scrapers show the actual tarot card artwork. */}
          <div data-testid="tarot-freedraw-share-bar">
            <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-200/70 mb-2 font-semibold text-center">Share this card</div>
          <div className="grid grid-cols-5 gap-2">
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="freedraw-share-whatsapp"
               title="Share on WhatsApp"
               className="flex items-center justify-center py-2.5 rounded-xl bg-green-600 text-white hover:bg-green-500 transition-transform hover:scale-105">
              <MessageCircle className="w-4 h-4" />
            </a>
            <a href={fbUrl} target="_blank" rel="noreferrer" data-testid="freedraw-share-facebook"
               title="Share on Facebook"
               className="flex items-center justify-center py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-500 transition-transform hover:scale-105">
              <Facebook className="w-4 h-4" />
            </a>
            <a href={twUrl} target="_blank" rel="noreferrer" data-testid="freedraw-share-twitter"
               title="Share on X (Twitter)"
               className="flex items-center justify-center py-2.5 rounded-xl bg-slate-900 text-white border border-slate-600 hover:bg-slate-800 transition-transform hover:scale-105">
              <Twitter className="w-4 h-4" />
            </a>
            <a href={tgUrl} target="_blank" rel="noreferrer" data-testid="freedraw-share-telegram"
               title="Share on Telegram"
               className="flex items-center justify-center py-2.5 rounded-xl bg-sky-500 text-white hover:bg-sky-400 transition-transform hover:scale-105">
              <Send className="w-4 h-4" />
            </a>
            <button
              data-testid="freedraw-copy-link"
              onClick={() => { try { navigator.clipboard.writeText(ogShareUrl); } catch (_) { /* clipboard blocked */ } }}
              title="Copy link"
              className="flex items-center justify-center py-2.5 rounded-xl bg-amber-500 text-black hover:bg-amber-400 transition-transform hover:scale-105"
            ><Link2 className="w-4 h-4" /></button>
          </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

// ============ Tarot Disclaimer (shown on spread page + reading page) ============
const Disclaimer = () => (
  <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 px-3 py-2 mb-4 flex items-start gap-2 text-[10px] sm:text-[11px] text-amber-100/80 leading-snug" data-testid="tarot-disclaimer">
    <Info className="w-3.5 h-3.5 text-amber-300 flex-shrink-0 mt-0.5" />
    <span>
      <b>Disclaimer:</b> This is an AI-powered, innovative, informative and experiential tool.
      Please take our results as <b>moral support</b> and as a <b>friend&apos;s suggestion</b>;
      take your own decisions accordingly. We are not liable for any loss, damage or incident
      in your personal life. Thanks 🙏
    </span>
  </div>
);

// ============ Card Grid ============
const CardGrid = ({ cards, selectedIds, onToggle, isShuffling }) => {
  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-10 gap-2 sm:gap-3" data-testid="tarot-cards-grid">
      {cards.map((c, idx) => {
        const isSelected = selectedIds.includes(c.cardId);
        const orderIdx = selectedIds.indexOf(c.cardId);
        // During shuffle: each card gets a randomized transform + jitter — HEAVIER animation
        const shuffleStyle = isShuffling ? {
          transform: `translateY(${Math.sin(idx * 1.3) * 60}px) translateX(${Math.cos(idx * 1.7) * 40}px) rotate(${(idx % 7 - 3) * 25}deg) scale(0.85)`,
          opacity: 0.5,
          filter: 'blur(2px)',
          zIndex: idx % 7 + 1,
        } : {};
        return (
          <button
            key={c.cardId}
            onClick={() => onToggle(c.cardId)}
            data-testid={`tarot-card-${c.cardId}`}
            disabled={isShuffling}
            style={shuffleStyle}
            className={`relative aspect-[2/3] rounded-lg overflow-hidden border-2 transform group ${
              isShuffling ? 'transition-all duration-700 ease-out' : 'transition-all duration-300'
            } ${
              isSelected
                ? 'border-amber-400 shadow-2xl shadow-amber-500/60 scale-105 rotate-1'
                : 'border-fuchsia-400/20 hover:border-fuchsia-400/60 hover:scale-105'
            }`}
          >
            {/* Card back — STAYS visible until reveal. Selected cards show only selection number. */}
            <div className="absolute inset-0">
              <div className={`w-full h-full bg-gradient-to-br ${isSelected ? 'from-amber-500 via-amber-600 to-orange-700' : 'from-violet-700 via-fuchsia-800 to-indigo-900'} flex items-center justify-center`}>
                {isSelected ? (
                  <div className="text-center">
                    <div className="text-4xl sm:text-5xl font-extrabold text-white drop-shadow-lg">{orderIdx + 1}</div>
                    <div className="text-[9px] sm:text-xs text-amber-100/90 font-bold uppercase tracking-wider mt-0.5">Selected</div>
                  </div>
                ) : (
                  <Sparkles className={`w-6 h-6 sm:w-8 sm:h-8 text-fuchsia-300/60 group-hover:text-fuchsia-300 transition ${isShuffling ? 'animate-spin' : ''}`} />
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ============ Reading View ============
const ReadingView = ({ reading, languages, onRestart, onShare, onSpeak, onDownload, speaking, publicSettings }) => {
  // Parse markdown-ish reading into sections
  const html = useMemo(() => {
    const txt = (reading.reading_text || '');
    return txt
      .split(/\n\n+/)
      .map((para, i) => {
        const formatted = para.replace(/\*\*(.+?)\*\*/g, '<span class="block text-amber-300 font-bold uppercase tracking-wider text-sm mb-1">$1</span>');
        return `<p key="${i}" class="mb-3 text-fuchsia-50/90 leading-relaxed">${formatted}</p>`;
      })
      .join('');
  }, [reading.reading_text]);

  const langName = languages?.find(l => l.code === (reading.language || 'en'))?.name || 'English';

  return (
    <div data-testid="tarot-reading-view" className="space-y-6">
      <Disclaimer />
      {/* Cards row — FRONT view of all 5 selected cards prominent */}
      <div className="bg-gradient-to-br from-violet-900/50 via-fuchsia-900/30 to-indigo-900/50 border border-fuchsia-400/30 rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="text-fuchsia-200 text-xs uppercase tracking-widest font-semibold">Your Question</div>
          <div className="flex gap-2 items-center">
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-violet-500/20 text-violet-200 border border-violet-400/30 flex items-center gap-1">
              <Globe className="w-3 h-3" /> {langName}
            </span>
            {reading.shuffle_count > 0 && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/20 text-amber-200 border border-amber-400/30">Shuffled {reading.shuffle_count}×</span>}
            {reading.prayed_at && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">🙏 Prayed</span>}
          </div>
        </div>
        <div className="text-white text-lg sm:text-xl font-semibold mb-5 italic">&ldquo;{reading.question}&rdquo;</div>
        <div className="grid grid-cols-5 gap-2 sm:gap-4">
          {(reading.cards || []).map((c, i) => (
            <div key={c.cardId} className="text-center" data-testid={`reading-card-${i}`}>
              <div className="aspect-[2/3] rounded-xl overflow-hidden border-2 border-amber-400/60 shadow-xl shadow-amber-500/30 relative bg-violet-900 mb-1">
                <img
                  src={c.image_url}
                  alt={c.name}
                  className="w-full h-full object-cover"
                  style={{ filter: 'saturate(1.25) contrast(1.1) brightness(1.05) drop-shadow(0 0 6px rgba(255,200,80,0.25))' }}
                />
                <div className="absolute top-1 left-1 w-6 h-6 rounded-full bg-amber-400 text-violet-950 text-xs font-extrabold flex items-center justify-center shadow">{i + 1}</div>
              </div>
              <div className="text-[10px] sm:text-xs text-amber-200 font-bold truncate">{c.name}</div>
              <div className="text-[9px] sm:text-[10px] text-fuchsia-200/70 font-semibold">{['Present', 'Illusion', 'Outside', 'Action', 'Awakening'][i]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Reading */}
      <div className="bg-gradient-to-br from-violet-900/40 via-indigo-900/30 to-violet-950/50 border border-fuchsia-400/30 rounded-2xl p-5 sm:p-7">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-fuchsia-200 text-sm uppercase tracking-widest font-semibold flex items-center gap-2">
            <Eye className="w-4 h-4" /> Zen Master&apos;s Reading
          </h3>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onSpeak} data-testid="reading-speak-btn" className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-1">
              {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              {speaking ? 'Stop' : 'Listen'}
            </button>
            <button onClick={onDownload} data-testid="reading-download-btn" className="px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 text-sm flex items-center gap-1">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={onShare} data-testid="reading-share-btn" className="px-3 py-1.5 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-sm flex items-center gap-1">
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button onClick={onRestart} data-testid="reading-restart-btn" className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-sm flex items-center gap-1">
              <RefreshCw className="w-4 h-4" /> New
            </button>
          </div>
        </div>
        <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
      </div>

      {/* Prominent "Send to my WhatsApp" CTA — viral lever */}
      <div className="rounded-2xl border-2 border-green-500/40 bg-gradient-to-r from-green-950 to-emerald-950 p-5 text-center" data-testid="whatsapp-send-cta">
        <MessageCircle className="w-8 h-8 text-green-300 mx-auto mb-2" />
        <h3 className="text-lg sm:text-xl font-extrabold text-green-200 mb-1">Send today&apos;s reading to your WhatsApp</h3>
        <p className="text-sm text-green-100/80 mb-4">Save it forever · share with someone you love · earn referral from 7 levels when they draw their own</p>
        <button
          onClick={onShare}
          data-testid="reading-whatsapp-send-btn"
          className="px-6 py-3 rounded-xl bg-green-500 hover:bg-green-400 text-black font-extrabold inline-flex items-center gap-2 shadow-lg"
        >
          <MessageCircle className="w-5 h-5" /> Send to WhatsApp
        </button>
      </div>

      {/* Additional Support — admin-editable WhatsApp helpdesk */}
      {publicSettings?.whatsapp_cta_url && (
        <a
          href={publicSettings.whatsapp_cta_url}
          target="_blank" rel="noreferrer"
          data-testid="tarot-support-whatsapp-cta"
          className="flex items-center justify-center gap-3 rounded-2xl border-2 border-green-500/50 bg-gradient-to-r from-green-600/25 to-emerald-600/25 hover:from-green-500/35 hover:to-emerald-500/35 transition p-4 sm:p-5 group"
        >
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-green-500 flex items-center justify-center group-hover:scale-110 transition">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div className="text-left">
            <div className="text-[10px] font-bold uppercase tracking-wider text-green-300">Additional Support</div>
            <div className="text-sm sm:text-base font-extrabold text-green-100">{publicSettings.whatsapp_cta_label || 'Chat with us on WhatsApp'}</div>
          </div>
        </a>
      )}

      {/* Distribution transparency */}
      {reading.distribution && (
        <details className="bg-white/5 border border-white/10 rounded-xl p-4 text-sm">
          <summary className="cursor-pointer text-fuchsia-200 font-semibold">View 7-Level Network Distribution</summary>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {reading.distribution.map(d => (
              <div key={d.level} className={`p-2 rounded-lg ${d.status === 'credited' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                <div className="font-bold text-fuchsia-100">L{d.level} — {d.share_pct}%</div>
                <div className="text-fuchsia-100/70">{d.amount} AT</div>
                <div className={`text-[10px] ${d.status === 'credited' ? 'text-emerald-300' : 'text-amber-300'}`}>{d.status === 'credited' ? '✓ credited' : 'bypassed → admin'}</div>
              </div>
            ))}
            <div className="p-2 rounded-lg bg-violet-500/10 border border-violet-500/30 col-span-2 sm:col-span-1">
              <div className="font-bold text-violet-200">Admin Pool</div>
              <div className="text-violet-100/70">{reading.admin_credit} AT</div>
            </div>
          </div>
        </details>
      )}

      {/* Sponsored Spiritual Tools — peak buying intent moment (right after reading) */}
      <SponsoredTools context="reading-end" maxItems={6} />
    </div>
  );
};
// ============ History View ============
const HistoryView = ({ history, onView, onShare }) => {
  if (!history.length) {
    return <div className="text-center py-12 text-fuchsia-100/60">No readings yet. Your inner journey awaits ✨</div>;
  }
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="tarot-history-view">
      {history.map(r => (
        <div key={r.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:border-fuchsia-400/40 transition" data-testid={`history-card-${r.id}`}>
          <div className="text-fuchsia-100/60 text-xs mb-1">{new Date(r.created_at).toLocaleString()}</div>
          <div className="text-white font-semibold text-base mb-2 line-clamp-2">&ldquo;{r.question}&rdquo;</div>
          <div className="flex gap-1 mb-3">
            {(r.cards || []).map((c, i) => (
              <div key={i} className="w-8 h-12 rounded overflow-hidden border border-amber-400/30" title={c.name}>
                <img
                  src={c.image_url}
                  alt={c.name}
                  className="w-full h-full object-cover"
                  style={{ filter: 'saturate(1.2) contrast(1.08) brightness(1.05)' }}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => onView(r)} className="px-3 py-1.5 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-xs flex items-center gap-1">
              <Eye className="w-3 h-3" /> View
            </button>
            <button onClick={() => onShare(r.id)} className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs flex items-center gap-1">
              <Share2 className="w-3 h-3" /> Share
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Share Modal ============
const ShareModal = ({ data, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-3" onClick={onClose} data-testid="tarot-share-modal">
    <div onClick={e => e.stopPropagation()} className="bg-violet-950 border border-fuchsia-400/40 rounded-2xl max-w-md w-full p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-lg text-white">Share Your Reading ✦</h3>
        <button onClick={onClose} className="text-fuchsia-200">✕</button>
      </div>
      <div className="bg-white/5 rounded-xl p-3 text-xs text-fuchsia-100/80 mb-3 whitespace-pre-line max-h-40 overflow-y-auto">{data.message}</div>
      {/* BIG WhatsApp button — primary action */}
      <a
        href={data.whatsapp_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-whatsapp-primary"
        className="block w-full mb-3 px-4 py-3 rounded-xl bg-green-500 hover:bg-green-400 text-black font-extrabold text-center inline-flex items-center justify-center gap-2"
      >
        <MessageCircle className="w-5 h-5" /> Send to WhatsApp
      </a>
      <div className="grid grid-cols-3 gap-2">
        <a href={data.telegram_url} target="_blank" rel="noopener noreferrer" data-testid="share-telegram" className="flex flex-col items-center gap-1 p-3 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300">
          <Send className="w-5 h-5" />
          <span className="text-xs">Telegram</span>
        </a>
        <a href={data.twitter_url} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="flex flex-col items-center gap-1 p-3 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/30 text-sky-300">
          <Twitter className="w-5 h-5" />
          <span className="text-xs">Twitter</span>
        </a>
        <button
          onClick={() => { navigator.clipboard.writeText(data.share_url); }}
          data-testid="share-copy-link"
          className="flex flex-col items-center gap-1 p-3 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300"
        >
          <Share2 className="w-5 h-5" />
          <span className="text-xs">Copy Link</span>
        </button>
      </div>
      <div className="mt-4 text-xs text-fuchsia-200/60">
        Your referral code: <span className="font-bold text-amber-300">{data.referral_code}</span> — earn from 7 levels of network commissions.
      </div>
    </div>
  </div>
);

export default AnantTarot;
